import type { AuthErrorRedirectFn } from '@repo/internal-tools-api/src/types/apiServiceTypes.ts'
import { composeBasePath } from '@repo/internal-tools-ts-utils/src/lib/composeBasePath.ts'
import { createContext, type ReactNode, useMemo } from 'react'
import { composeLoginPathWithRedirectUrl } from '../composeLoginPathWithRedirectUrl.ts'

export type GenericAppContextValue = {
  /**
   * Raw base path from ENV.
   * If you use it directly to compose with other path, you might run into `//some-path` issue.
   */
  basePath: string
  /**
   * Processed basePath with `composeBasePath` ready to be used for composing with other paths.
   */
  processedBasePath: string
  baseApiUrl: string
  baseSignalrUrl: string
  authErrorRedirect: AuthErrorRedirectFn
}

export type ComposeGenericAppContextValueParams = {
  input: Partial<GenericAppContextValue>
  defaultValues?: Partial<GenericAppContextValue>
}

// eslint-disable-next-line react-refresh/only-export-components
export const composeGenericAppContextValue = (
  params: ComposeGenericAppContextValueParams,
): GenericAppContextValue => {
  const { input = {}, defaultValues = {} } = params

  const basePath = input?.basePath ?? defaultValues?.basePath ?? '/'

  const processedBasePath =
    input?.processedBasePath ??
    defaultValues?.processedBasePath ??
    composeBasePath(basePath)

  /**
   * Redirect to the login page when an auth error occurs.
   */
  const authErrorRedirect =
    input?.authErrorRedirect ??
    defaultValues?.authErrorRedirect ??
    // Default behavior to redirect using native browser navigation.
    (() => {
      window.location.href = composeLoginPathWithRedirectUrl(processedBasePath)
    })

  return {
    basePath,
    processedBasePath,
    authErrorRedirect,
    baseApiUrl:
      input?.baseApiUrl ?? defaultValues?.baseApiUrl ?? 'NO_API_URL_PROVIDED',
    baseSignalrUrl:
      input?.baseSignalrUrl ??
      defaultValues?.baseSignalrUrl ??
      'NO_SIGNALR_URL_PROVIDED',
  }
}

// The value is partial to make the provider accept partial data.
// The hook will handle necessary computation to make it match
// the required context value shape.
//
// eslint-disable-next-line react-refresh/only-export-components
export const GenericAppContext = createContext<
  GenericAppContextValue | undefined
>(undefined)

export type GenericAppContextProviderProps = Partial<GenericAppContextValue> & {
  children?: ReactNode
}

export const GenericAppContextProvider = (
  props: GenericAppContextProviderProps,
) => {
  const { children, ...contextProps } = props

  // Memoize value depending on each value instead of the object reference.
  const contextValue = useMemo(
    () => composeGenericAppContextValue({ input: contextProps }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.values(contextProps),
  )

  return (
    <GenericAppContext.Provider value={contextValue}>
      {children}
    </GenericAppContext.Provider>
  )
}
